import type { TableProps } from "antd";
import { Col, Input, Row, Table, Typography } from "antd";
import React, { useState } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import "./index.css";
import { ButtonProps } from "../../../helpers/constants";
import { formatDate } from "../../../helpers/date";
import { getUserType } from "../../../helpers/userDetails";
import useMultiNotification from "../../../hooks/useNotification";
import ReOpenConfirmationModal from "../../Modals/ReOpenConfirmationModal";
import ActionQueueProcessConfirmation from "../ActionQueueUtils/ActionQueueConfirmation";
import ActionQueueProcessRejection from "../ActionQueueUtils/ActionQueueRjection";
import NetwalaButton from "../Button/Index";
import IPTVIcon from "./../../../assets/svgs/IPTVIcon.svg";
import IPWhiteListIcon from "./../../../assets/svgs/IPWhiteList.svg";
import CopyIcon from "./../../../assets/svgs/CopyIcon.svg";

const { Search } = Input;

const ActionQueueTable: React.FC<OperatorActionQueueTable> = ({
  queues,
  handleAcceptAction,
  handleRejectionAction,
  activeTab,
  handleReOpenItem,
  searchTerm = "",
  onSearchChange,
  settings,
}) => {
  const role = getUserType();
  const { openNotification, contextHolder } = useMultiNotification();

  const isShowPassword = (password: string) => {
    if (!password) {
      return false;
    }
    // CEO and PANEL_MANAGER always see passwords
    if (role === "CEO" || role === "PANEL_MANAGER") {
      return true;
    }
    // If toggle is ON, show password
    if (settings && settings.usersPasswordViewToRman === 1) {
      return true;
    }
    return false;
  };
  const [activeRow, setActiveRow] = useState<OperatorActionQueue | null>(null);
  const [showProcess, setShowProcess] = useState<boolean>(false);
  const [showRejection, setShowRejection] = useState<boolean>(false);
  const [showReOpen, setShowReOpen] = useState<boolean>(false);

  const handleClose = (isReload: boolean) => {
    setShowProcess(false);
    setShowRejection(false);
    setShowReOpen(false);
    setActiveRow(null);
  };

  const handleAcceptClick = (row: OperatorActionQueue) => {
    setActiveRow(row);
    setShowProcess(true);
  };

  const handleRejectClick = (row: OperatorActionQueue) => {
    setActiveRow(row);
    setShowRejection(true);
  };

  const columns: TableProps<OperatorActionQueue>["columns"] = [
    {
      title: "S.No",
      dataIndex: "intPkOperatorActionQueueID",
      key: "intPkOperatorActionQueueID",
      width: 150,
      render: (text) => <span>{text < 10 ? `0${text}` : text}</span>,
    },
    {
      title: "Name",
      width: 150,
      render: (row) => (
        <span>
          {row.firstName} {row.lastName}
        </span>
      ),
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      width: 150,
    },
    {
      title: "Connection ID",
      dataIndex: "username",
      key: "username",
      width: 150,
      render: (text: string) => (
        <Typography
          style={{ display: "flex", alignItems: "center", gap: "10px" }}
        >
          {text}
          <CopyToClipboard
            text={text || ""}
            onCopy={() => {
              openNotification(
                "success",
                "Copied",
                "Username copied to clipboard"
              );
            }}
          >
            <img
              onClick={(e) => {
                e.preventDefault();
              }}
              src={CopyIcon}
              className="cursor-pointer"
              style={{ width: "16px", height: "16px" }}
            />
          </CopyToClipboard>
        </Typography>
      ),
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      width: 150,
    },
    {
      title: "Password",
      dataIndex: "password",
      key: "password",
      width: 150,
      render: (text: string) => {
        const shouldShowPassword = isShowPassword(text);
        const displayPassword = shouldShowPassword ? text : "---";
        return (
          <Typography
            style={{ display: "flex", alignItems: "center", gap: "10px" }}
          >
            {displayPassword}
            {shouldShowPassword && (
              <CopyToClipboard
                text={text || ""}
                onCopy={() => {
                  openNotification(
                    "success",
                    "Copied",
                    "Password copied to clipboard"
                  );
                }}
              >
                <img
                  onClick={(e) => {
                    e.preventDefault();
                  }}
                  src={CopyIcon}
                  className="cursor-pointer"
                  style={{ width: "16px", height: "16px" }}
                />
              </CopyToClipboard>
            )}
          </Typography>
        );
      },
    },
    {
      title: "ISP",
      dataIndex: "ISPName",
      key: "ISPName",
      width: 150,
    },
    {
      title: "Package",
      width: 150,
      render: (row) => (
        <div
          className="package-color-container"
          style={{
            color: row.color,
            background: row.bgColor,
            border: "1px solid",
            borderColor: row.borderColor,
          }}
        >
          {row.actionQueuePackageName || row.packageName}
        </div>
      ),
    },
    {
      title: "Vlan",
      dataIndex: "vlanValue",
      key: "vlanValue",
      width: 150,
    },
    {
      title: "Activity Type",
      dataIndex: "actionQueueType",
      key: "actionQueueType",
      width: 150,
    },
    {
      title: "Addons",
      align: "right",
      width: 150,
      render: (row) => (
        <div className="users-table-action-container">
          {row.totalIPTVServices > 0 ? (
            <img src={IPTVIcon} className="cursor-pointer" />
          ) : null}
          {row.totalStaticIpServices > 0 ? (
            <img src={IPWhiteListIcon} className="cursor-pointer" />
          ) : null}
        </div>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (text) => <span>{formatDate(new Date(text))}</span>,
    },
  ];

  if (activeTab === 1) {
    if (role === "PANEL_MANAGER" || role === "CEO") {
      columns.push({
        title: "Action",
        width: 200,
        render: (row) => (
          <div style={{ display: "flex", gap: "8px" }}>
            <NetwalaButton
              backgroundColor="linear-gradient(96.95deg, #52c41a 1.36%, #73d13d 96.17%)"
              height={"39px"}
              width={"90px"}
              textColor={ButtonProps.color.STANDARD}
              onClick={() => handleAcceptClick(row)}
              text="Accept"
              borderColor="#52c41a"
            />
            <NetwalaButton
              backgroundColor={ButtonProps.background.DANGER}
              height={"39px"}
              width={"90px"}
              textColor={ButtonProps.color.STANDARD}
              onClick={() => handleRejectClick(row)}
              text="Reject"
              borderColor="#DF3131"
            />
          </div>
        ),
      });
    }
  } else if (activeTab === 2) {
    columns.push({
      title: "Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 150,
      render: (text) => <span>{formatDate(new Date(text))}</span>,
    });
  } else if (activeTab === 3) {
    columns.push({
      title: "Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 150,
      render: (text) => <span>{formatDate(new Date(text))}</span>,
    });
    columns.push({
      title: "Rejection Reason",
      dataIndex: "rejectionReason",
      key: "rejectionReason",
      width: 150,
    });
    if (role !== "PANEL_MANAGER") {
      columns.push({
        title: "Action",
        width: 150,
        render: (row) => (
          <NetwalaButton
            backgroundColor={ButtonProps.background.STANDARD}
            height={"39px"}
            width={"100px"}
            textColor={ButtonProps.color.STANDARD}
            onClick={() => {
              setActiveRow(row);
              setShowReOpen(true);
            }}
            text="ReOpen"
          />
        ),
      });
    }
  }

  const confirmationButton = (isProcess: boolean, ip?: string) => {
    setShowProcess(false);
    if (isProcess === true && activeRow !== null) {
      handleAcceptAction(activeRow.intPkOperatorActionQueueID, ip);
    }
  };

  const handleReject = (isReject: boolean, value: string) => {
    setShowRejection(false);
    if (isReject === true && activeRow !== null) {
      handleRejectionAction(activeRow.intPkOperatorActionQueueID, value);
    }
  };

  const handleReopen = (isOpen: boolean) => {
    if (isOpen === true && activeRow) {
      handleReOpenItem(activeRow.intPkOperatorActionQueueID);
    }
    setActiveRow(null);
    setShowReOpen(false);
  };

  const rowClassName = (record: any, index: any) => {
    if (record.actionQueueType === "NEW_CONNECTION") {
      return "new-connection-highlighted-row";
    }
    if (record.actionQueueType === "CHANGE_PACKAGE") {
      return "change-package-highlighted-row";
    }
    if (
      record.actionQueueType === "REMOVE_IPTV" ||
      record.actionQueueType === "REMOVE_STATIC_IP"
    ) {
      return "remove-package-highlighted-row";
    }
    return "";
  };

  return (
    <>
      {contextHolder}
      {showRejection && (
        <ActionQueueProcessRejection
          show={showRejection}
          handleClose={handleClose}
          handleReject={handleReject}
        />
      )}

      {showReOpen && activeRow && (
        <ReOpenConfirmationModal show={showReOpen} handleClose={handleReopen} />
      )}
      {showProcess && activeRow && (
        <ActionQueueProcessConfirmation
          show={showProcess}
          handleClose={handleClose}
          confirmationButton={confirmationButton}
          actionQueueType={activeRow.actionQueueType}
        />
      )}

      <div className="netwala-users-table-container">
        <Row>
          <Col span={24}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <Typography className="netwala-user-tables-heading">
                Action Queue
              </Typography>
              <Search
                placeholder="Search by Connection ID (Username)"
                onSearch={(value) => onSearchChange?.(value)}
                value={searchTerm}
                allowClear
                onChange={(e) => onSearchChange?.(e.target.value)}
                style={{ width: 300 }}
              />
            </div>
            <Table
              className="netwala-users-table striped-table"
              columns={columns}
              dataSource={queues}
              pagination={false}
              rowClassName={rowClassName}
              scroll={{ x: "max-content" }} // 👈 horizontal scroll enabled
            />
          </Col>
        </Row>
      </div>
    </>
  );
};

export default ActionQueueTable;
